import { memo, useCallback, useEffect } from 'react';
import {
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import ConfirmElement from '../siteSections/pageElements/ConfirmElements';
import StepperButtons from './StepperButtons';
import StripeRoot from '../utils/StripeRoot';
import StripeInput from '../utils/StripeInput';
import { Form, Field, FormError } from '../utils/form';
import { FORM_ERROR } from 'final-form';
import countries from './countries';
import ReactPixel from 'react-facebook-pixel';

// ---------------------------------------------------------------------------
// Tailwind input estilo "boxed" — igual que en RegisterContact             
// ---------------------------------------------------------------------------
const InputField = ({
  label,
  type = 'text',
  select = false,
  children,
  error,
  helperText,
  InputProps,
  inputProps,
  value,
  onChange,
  onBlur,
  onFocus,
  required,
  id,
  name,
  ...rest
}) => {
  const placeholder = `${label}${required ? ' *' : ''}`;
  const baseInputClasses = `w-full rounded border px-3 py-2 bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 ${error ? 'border-red-500 focus:ring-red-500' : 'border-gray-400 focus:ring-[#00897b]'}`;
  const { autoComplete, ...restProps } = rest;

  if (InputProps?.inputComponent) {
    // Allow Stripe Elements to plug into the shared field template while keeping react-final-form hooks.
    const StripeComponent = InputProps.inputComponent;
    const {
      className: stripeClassName = '',
      onChange: stripeOnChange,
      onBlur: stripeOnBlur,
      onFocus: stripeOnFocus,
      ...stripeRest
    } = inputProps || {};

    const handleStripeChange = (event) => {
      if (stripeOnChange) stripeOnChange(event);
      if (onChange) onChange(event);
    };

    const handleStripeBlur = (event) => {
      if (stripeOnBlur) stripeOnBlur(event);
      if (onBlur) onBlur(event);
    };

    const handleStripeFocus = (event) => {
      if (stripeOnFocus) stripeOnFocus(event);
      if (onFocus) onFocus(event);
    };

    return (
      <div className="w-full">
        <div
          id={id}
          className={`w-full rounded border px-3 py-2 bg-white text-gray-900 ${error ? 'border-red-500 focus-within:ring-2 focus-within:ring-red-500' : 'border-gray-400 focus-within:ring-2 focus-within:ring-[#00897b]'}`}
        >
          <StripeComponent
            {...stripeRest}
            placeholder={placeholder}
            onChange={handleStripeChange}
            onBlur={handleStripeBlur}
            onFocus={handleStripeFocus}
            autoComplete={autoComplete}
            className={`w-full ${stripeClassName}`.trim()}
          />
        </div>
        {helperText && (
          <span className={`mt-1 text-xs ${error ? 'text-red-500' : 'text-gray-500'} block`}>{helperText}</span>
        )}
      </div>
    );
  }

  return (
    <div className="w-full">
      {select ? (
        <select
          id={id}
          name={name}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          onFocus={onFocus}
          placeholder={placeholder}
          className={baseInputClasses}
          required={required}
          autoComplete={autoComplete}
          {...restProps}
        >
          {children}
        </select>
      ) : (
        <input
          id={id}
          name={name}
          type={type}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          onFocus={onFocus}
          placeholder={placeholder}
          className={baseInputClasses}
          required={required}
          autoComplete={autoComplete}
          {...restProps}
        />
      )}
      {helperText && (
        <span className={`mt-1 text-xs ${error ? 'text-red-500' : 'text-gray-500'} block`}>{helperText}</span>
      )}
    </div>
  );
};

const TermsAgreementToggle = ({ value, onChange, helperText, error }) => (
  <div className="flex flex-col items-start">
    <label className="inline-flex items-center cursor-pointer select-none">
      <input
        type="checkbox"
        checked={value}
        onChange={() => onChange(!value)}
        className="h-5 w-5 accent-[#9aeeda] rounded focus:ring-0"      />
      <span className="ml-2">I agree with the terms and conditions</span>
    </label>
    {helperText && (
      <span className={`mt-1 text-xs ${error ? 'text-red-500' : 'text-gray-500'}`}>
        {helperText}
      </span>
    )}
  </div>
);

const required = (msg) => (v) => (!v ? msg : undefined);

function RegisterConfirmStep({ data, nextStep, stepperProps }) {
  const stripe = useStripe();
  const elements = useElements();

  const { email, phoneNumber, paymentIntentClientSecret } = data;

  const ccValueOnChange = useCallback((e) => (e.complete ? '****' : ''), []);

  const validateCcNumberRequired = required('Card number is required');
  const validateCcExpiryRequired = required('Card expiry is required');
  const validateCcCvcRequired = required('Card CVC is required');
  const validatePostalCodeRequired = required('Card postal code is required');
  const validateCountryRequired = required('Card country is required');
  const validateTermsAgreement = required('Please read the terms first');

  const submit = useCallback(
    async (billingInfo) => {
      if (!stripe || !elements) return { [FORM_ERROR]: 'Stripe is not ready' };
      const cardElement = elements.getElement(CardNumberElement);
      const {
        billingName,
        addressLine1,
        addressLine2,
        addressCity,
        addressState,
        addressPostalCode,
        addressCountry,
      } = billingInfo;
      try {
        const confirmOptions = {
          payment_method: {
            card: cardElement,
            billing_details: {
              name: billingName,
              email,
              phone: phoneNumber,
              address: {
                line1: addressLine1,
                line2: addressLine2,
                city: addressCity,
                state: addressState,
                postal_code: addressPostalCode,
                country: addressCountry,
              },
            },
          },
          receipt_email: email,
          save_payment_method: true,
          setup_future_usage: 'off_session',
        };

        let { error, paymentIntent } = await stripe.confirmCardPayment(paymentIntentClientSecret, confirmOptions);
        if (error) return { [FORM_ERROR]: error.message };

        if (paymentIntent?.status === 'requires_action' && paymentIntent.next_action?.type === 'use_stripe_sdk') {
          const actionResult = await stripe.confirmCardPayment(paymentIntentClientSecret);
          if (actionResult.error) return { [FORM_ERROR]: actionResult.error.message };
          paymentIntent = actionResult.paymentIntent;
        }

        if (!paymentIntent) return { [FORM_ERROR]: 'Payment confirmation returned no intent' };

        if (paymentIntent.status === 'requires_payment_method') {
          return { [FORM_ERROR]: 'Authentication failed. Please try a different payment method.' };
        }

        if (paymentIntent.status !== 'succeeded') {
          return { [FORM_ERROR]: `Payment failed with status: ${paymentIntent.status}` };
        }

        nextStep({ ...billingInfo, cardPaymentIntent: paymentIntent });
      } catch (err) {
        console.error(err);
        return { [FORM_ERROR]: 'An unexpected error occurred' };
      }
    },
    [stripe, elements, email, phoneNumber, paymentIntentClientSecret, nextStep]
  );

  const initialValues = {
    ...data,
    billingName: data.billingName || `${data.firstName} ${data.lastName}`,
  };

  return (
    <Form initialValues={initialValues} onSubmit={submit}>
      {() => (
        <>
          {/* Columns */}
          <div className="mx-4 flex flex-col sm:flex-row gap-6 text-left">
            {/* Confirmation */}
            <div className="max-w-[350px]">
              <h2 className="text-xl font-semibold mt-6 mb-5">Confirm your information is correct</h2>
              <h3 className="font-bold text-sm mb-1">Contact Details</h3>
              <hr className="mb-2" />
              <div className="flex gap-2">
                <ConfirmElement label="First Name" item={data.firstName} />
                <ConfirmElement label="Last Name" item={data.lastName} />
              </div>
              <div className="flex gap-2">
                <ConfirmElement label="Email" item={data.email} />
                <ConfirmElement label="Phone Number" item={data.phoneNumber} />
              </div>
              <h3 className="font-bold text-sm mt-4 mb-1">Selection</h3>
              <hr className="mb-2" />
              <ConfirmElement label="January 2027 Online Retreat Deposit" item={data.liveStreamingPass} />
            </div>
            {/* Payment */}
            <div className="flex-1">
              <h2 className="text-xl font-semibold mt-6 mb-5">Enter your payment information</h2>
              {/* Cardholder name */}
              <div className="mt-2">
                <Field component={InputField} id="billing-name" name="billingName" label="Cardholder Name" autoComplete="billing cc-name" required />
              </div>
              {/* Card number */}
              <div className="mt-2">
                <Field
                  component={InputField}
                  id="cc-number"
                  name="ccNumber"
                  label="Credit Card Number"
                  InputProps={{ inputComponent: StripeInput }}
                  inputProps={{ component: CardNumberElement }}
                  validate={validateCcNumberRequired}
                  valueOnChange={ccValueOnChange}
                  required
                />
              </div>
              {/* Expiry & CVC */}
              <div className="mt-2 grid grid-cols-2 gap-2">
                <Field
                  component={InputField}
                  id="cc-expiry"
                  name="ccExpiry"
                  label="Expiry"
                  InputProps={{ inputComponent: StripeInput }}
                  inputProps={{ component: CardExpiryElement }}
                  validate={validateCcExpiryRequired}
                  valueOnChange={ccValueOnChange}
                  required
                />
                <Field
                  component={InputField}
                  id="cc-csc"
                  name="ccCsc"
                  label="Security Code"
                  InputProps={{ inputComponent: StripeInput }}
                  inputProps={{ component: CardCvcElement }}
                  validate={validateCcCvcRequired}
                  valueOnChange={ccValueOnChange}
                  required
                />
              </div>
              {/* Billing address */}
              <h3 className="text-lg font-medium mt-6 mb-2">Billing address</h3>
              <Field component={InputField} id="billing-line1" name="addressLine1" label="Street Address" autoComplete="billing address-line1" required />
              <div className="mt-2">
                <Field component={InputField} id="billing-line2" name="addressLine2" label="Address Line 2 (Optional)" autoComplete="billing address-line2" />
              </div>
              <div className="mt-2 grid grid-cols-2 gap-2">
                <Field component={InputField} id="billing-city" name="addressCity" label="City" autoComplete="billing address-level2" required />
                <Field component={InputField} id="billing-state" name="addressState" label="State / Province" autoComplete="billing address-level1" required />
              </div>
              <div className="mt-2 grid grid-cols-2 gap-2">
                <Field
                  component={InputField}
                  id="billing-postalcode"
                  name="addressPostalCode"
                  label="Postal Code"
                  autoComplete="billing postal-code"
                  validate={validatePostalCodeRequired}
                  required
                />
                <Field
                  component={InputField}
                  id="billing-country"
                  name="addressCountry"
                  label="Country"
                  autoComplete="billing country"
                  select
                  validate={validateCountryRequired}
                  required
                >
                  <option value=""></option>
                  {countries.map(({ code, label }) => (
                    <option key={code} value={code}>
                      {label}
                    </option>
                  ))}
                </Field>
              </div>
            </div>
          </div>

          {/* Policies & terms */}
          <div className="m-4 p-4 bg-white rounded-lg shadow text-left">
            <h4 className="font-semibold mb-2">Registration Payment Policy:</h4>
            <ul className="list-disc list-inside text-xs space-y-1 mb-4 ml-6">
              <li>$695 deposit (plus 3.75% credit card processing fee) due with registration.</li>
              <li>Payment by accepted credit cards or check (made payable to "Geneen Roth & Associates Inc.").</li>
              <li>Balance of retreat fees is due September 19, 2026. If paying by credit card, balance due on that date will be charged to the credit card in our system. (with 3.75% processing fee added).</li>
            </ul>
            <h4 className="font-semibold mb-2">Cancellation Policy:</h4>
            <ul className="list-disc list-inside text-xs space-y-1 mb-4 ml-6">
              <li>Cancellations must be requested in writing to "Geneen Roth & Associates, Inc." (email, fax, or USPS).</li>
              <li>Cancellations received by September 19, 2026, you will receive a full refund, less the non-refundable deposit and any credit card processing fees paid.</li>
              <li>Cancellations received after September 19, 2026: no refunds, transfers or credits.</li>
            </ul>
            <p className="text-xs">
              This retreat will be video recorded for review by all participants for 30 days following the end of the online retreat. I acknowledge and agree that GR&A may at its sole discretion use these recordings, screen shot photography or audio files from the online retreat, including the participation of any or all participants therein (including you, unless otherwise agreed in advance to avoid your image and voice), in any and all media, provided that GR&A will take reasonable efforts to disguise my personal identity.
            </p>
            <div className="mt-3 flex justify-end">
              <Field component={TermsAgreementToggle} type="checkbox" name="termsAgreement" validate={validateTermsAgreement} />
            </div>
          </div>

          <FormError className="mx-4" />
          <StepperButtons {...stepperProps} />
        </>
      )}
    </Form>
  );
}

function RegisterConfirm(props) {
  useEffect(() => {
    ReactPixel.track('AddToCart');
  }, []);

  return (
    <StripeRoot>
      <RegisterConfirmStep {...props} />
    </StripeRoot>
  );
}

export default memo(RegisterConfirm);
